import { useState, type FormEvent } from 'react';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

interface ServerResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const FORM_ENDPOINT = 'https://readdy.ai/api/form/dainbejoh653ivfvogv0';

const benefits = [
  { icon: 'ri-calendar-check-line', text: '每周一期，汇总本周新增与修订的话题条目' },
  { icon: 'ri-filter-3-line', text: '按你勾选的分区推送，不必翻遍整个目录' },
  { icon: 'ri-shield-check-line', text: '仅用于发送简报，可随时一键取消订阅' },
];

export default function SubscribeSection() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honey = String(formData.get('website_alt') ?? '').trim();

    if (honey) {
      setStatus('success');
      setFormError('');
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const str = typeof value === 'string' ? value : '';
      if (str.trim() === '') return;
      params.append(key, str);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: ServerResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ServerResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const code = parsed?.code;
      const isSpam = serverMsg.toLowerCase().includes('spam');

      if (!response.ok || code !== 'OK' || isSpam) {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
        return;
      }

      setStatus('success');
      setFormError('');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <section id="subscribe" className="w-full bg-foreground-950 py-14 md:py-20">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-4 md:px-8 lg:grid-cols-[1fr_1fr] lg:items-center">
        <div className="reveal">
          <span className="text-xs font-semibold tracking-[0.3em] text-accent-400">NEWSLETTER</span>
          <h2 className="mt-2 font-heading text-2xl font-black text-background-50 md:text-4xl">
            订阅黑料速递
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-300">
            留下邮箱，每周收到一份目录简报：新增话题、修订记录与分区动态。也可以直接在下方给编辑团队留言。
          </p>

          <ul className="mt-7 space-y-3.5">
            {benefits.map((b) => (
              <li key={b.text} className="flex items-start gap-3 text-sm text-background-200">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-800 text-accent-400">
                  <i className={b.icon}></i>
                </span>
                <span className="pt-1 text-xs leading-relaxed md:text-sm">{b.text}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-background-800 pt-5 text-xs text-background-400">
            <span className="flex items-center gap-2">
              <i className="ri-mail-line text-accent-400"></i>
              contact@44-heiliao.com
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-time-line text-accent-400"></i>
              工作日 10:00 - 19:00 回复
            </span>
          </div>
        </div>

        <div className="reveal rounded-lg border border-background-700 bg-background-50 p-5 md:p-7">
          <form
            id="heiliao-subscribe-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <div>
              <label htmlFor="sub-email" className="block text-xs font-semibold text-foreground-800">
                邮箱地址 <span className="text-primary-600">*</span>
              </label>
              <input
                id="sub-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@example.com"
                className="mt-1.5 w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>

            <div>
              <label htmlFor="sub-interest" className="block text-xs font-semibold text-foreground-800">
                关注分区
              </label>
              <select
                id="sub-interest"
                name="interest"
                defaultValue="全部速递"
                className="mt-1.5 w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              >
                <option value="全部速递">全部速递</option>
                <option value="明星话题">明星话题</option>
                <option value="网红圈">网红圈</option>
                <option value="政商观察">政商观察</option>
                <option value="社会热点">社会热点</option>
                <option value="影视风云">影视风云</option>
                <option value="综艺现场">综艺现场</option>
              </select>
            </div>

            <div>
              <label htmlFor="sub-message" className="block text-xs font-semibold text-foreground-800">
                留言或纠错（选填）
              </label>
              <textarea
                id="sub-message"
                name="message"
                rows={3}
                maxLength={500}
                placeholder="例如：某条目录信息需要更正，附上可核实的公开来源。"
                className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              ></textarea>
              <p className="mt-1 text-[11px] text-foreground-500">最多 500 字</p>
            </div>

            <input
              type="text"
              name="website_alt"
              className="newsletter-consent-helper"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {status === 'submitting' ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  提交中…
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i>
                  提交订阅 / 留言
                </>
              )}
            </button>

            {status === 'success' && (
              <p className="flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-3.5 py-2.5 text-xs leading-relaxed text-accent-900">
                <i className="ri-checkbox-circle-line text-sm"></i>
                提交成功，感谢你的关注。我们会在每周简报中与你见面。
              </p>
            )}
            {status === 'error' && (
              <p className="flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-2.5 text-xs leading-relaxed text-primary-800">
                <i className="ri-error-warning-line text-sm"></i>
                {formError || '提交失败，请稍后重试。'}
              </p>
            )}
            <p className="text-center text-[11px] leading-relaxed text-foreground-500">
              提交即表示同意我们仅将你的邮箱用于发送目录简报，不会用于其他用途。
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}